// client/src/hooks/useDivisionAssignments.js
import { useState, useMemo, useCallback } from 'react'
import toastr from 'toastr'
import { getDivisionAssignmentRestrictedMessage } from '../constants/leagueStatuses'

/**
 * Custom hook to manage division assignments and drag-and-drop
 *
 * Handles new division input state, player assignment validation,
 * and drag-and-drop logic for assigning players to divisions.
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.players - Array of all players
 * @param {Object} options.assignments - Current assignments map
 * @param {Function} options.setAssignments - Function to update assignments
 * @param {Object} options.selectedLeague - Currently selected league
 * @param {boolean} options.canEditDivisionsForLeague - Whether editing is allowed
 * @returns {Object} Division assignment state and handlers
 */
export default function useDivisionAssignments({
  players,
  assignments,
  setAssignments,
  selectedLeague,
  canEditDivisionsForLeague,
}) {
  const [newDivision, setNewDivision] = useState({
    division_name: '',
    rank_level: '',
    gender: '',
  })

  // Handle input changes for new division form
  const handleDivisionInputChange = useCallback((e) => {
    const { name, value } = e.target
    setNewDivision((prev) => ({ ...prev, [name]: value }))
  }, [])

  // Reset new division form to empty state
  const resetNewDivision = useCallback(() => {
    setNewDivision({
      division_name: '',
      rank_level: '',
      gender: '',
    })
  }, [])

  // Check if there are unsaved player assignment changes
  const hasUnsavedChanges = useCallback(() => {
    const currentMap = {}
    for (const [divId, ids] of Object.entries(assignments)) {
      for (const playerId of ids) currentMap[playerId] = parseInt(divId)
    }
    const knownAssignments = {}
    for (const p of players) {
      if (p.division_id !== null) knownAssignments[p.id] = p.division_id
    }

    // Check for removed assignments
    for (const id in knownAssignments) {
      if (!(id in currentMap)) return true
    }

    // Check for new/changed assignments
    for (const [id, divId] of Object.entries(currentMap)) {
      if (knownAssignments[id] !== divId) return true
    }

    return false
  }, [assignments, players])

  // Get list of unassigned players
  const getUnassigned = useCallback(() => {
    const assigned = Object.values(assignments).flat()
    return players.filter((p) => !assigned.includes(p.id))
  }, [assignments, players])

  // Memoized unassigned players list
  const unassignedPlayers = useMemo(() => getUnassigned(), [getUnassigned])

  // Handle drag end event from dnd-kit
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event
      if (!active || !over) return

      // Check if league is in an editable state
      if (selectedLeague && !canEditDivisionsForLeague) {
        toastr.error(
          `Cannot modify division assignments: ${getDivisionAssignmentRestrictedMessage(
            selectedLeague.league_status,
          )}`,
        )
        return
      }

      const dragId = active.id
      const divisionId = over.id

      // Resolve player IDs — team cards use "team-{teamId}-{playerId}" prefix
      const isTeam = typeof dragId === 'string' && dragId.startsWith('team-')
      let playerIds
      if (isTeam) {
        const teamId = Number(dragId.split('-')[1])
        playerIds = players
          .filter((p) => p.partner?.team_id === teamId)
          .map((p) => p.id)
      } else {
        playerIds = [dragId]
      }

      if (playerIds.length === 0) return

      setAssignments((prev) => {
        const updated = { ...prev }
        // Remove player(s) from all divisions
        for (const key in updated) {
          updated[key] = updated[key].filter((id) => !playerIds.includes(id))
        }
        // Add to new division if not unassigned
        if (divisionId !== 'unassigned') {
          // Enforce max players per division during drag-and-drop
          const max = Number(selectedLeague?.max_players_per_division)
          if (Number.isFinite(max) && max > 0) {
            const currentCount = updated[divisionId]?.length || 0
            if (currentCount + playerIds.length > max) {
              toastr.error(
                `Cannot add ${isTeam ? 'team' : 'player'}: Division would exceed max ${max} players.`,
              )
              return prev
            }
          }
          if (!updated[divisionId]) updated[divisionId] = []
          updated[divisionId].push(...playerIds)
        }
        return updated
      })
    },
    [selectedLeague, canEditDivisionsForLeague, setAssignments, players],
  )

  return {
    // New division state
    newDivision,
    handleDivisionInputChange,
    resetNewDivision,

    // Assignment validation
    hasUnsavedChanges,
    unassignedPlayers,

    // Drag-and-drop handler
    handleDragEnd,
  }
}
